"use client";

/**
 * Scrubber with scrub-intent display time and throttled element seeks.
 */

import { useTranslations } from "next-intl";
import type { ReactElement, PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { SCRUB_SEEK_THROTTLE_MS } from "./clip-video-player-types";
import { clampMediaTime } from "./derive-clip-media-ui";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function ClipVideoScrubber({
  duration,
  displayTime,
  progressRatio,
  bufferedRatio,
  onScrubbingChange,
  onScrubTimeChange,
  onSeek,
}: {
  readonly duration: number;
  readonly displayTime: number;
  readonly progressRatio: number;
  readonly bufferedRatio: number;
  readonly onScrubbingChange: (scrubbing: boolean) => void;
  readonly onScrubTimeChange: (time: number | null) => void;
  readonly onSeek: (time: number) => void;
}): ReactElement {
  const t = useTranslations("clips.clipVideoScrubber");
  const trackRef = useRef<HTMLDivElement>(null);
  const scrubbingRef = useRef(false);
  const lastSeekAtRef = useRef(0);
  const pendingTimeRef = useRef<number | null>(null);
  const [scrubbing, setScrubbing] = useState(false);

  useEffect(() => {
    scrubbingRef.current = scrubbing;
  }, [scrubbing]);

  const timeFromClientX = useCallback(
    (clientX: number): number | null => {
      const track = trackRef.current;
      if (!track || !Number.isFinite(duration) || duration <= 0) return null;
      const rect = track.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      return clampMediaTime(ratio * duration, duration);
    },
    [duration],
  );

  const commitSeek = useCallback(
    (time: number, force: boolean): void => {
      const now = Date.now();
      if (!force && now - lastSeekAtRef.current < SCRUB_SEEK_THROTTLE_MS) {
        pendingTimeRef.current = time;
        return;
      }
      lastSeekAtRef.current = now;
      pendingTimeRef.current = null;
      onSeek(time);
    },
    [onSeek],
  );

  // Flush throttled seek shortly after the last drag move.
  useEffect(() => {
    if (!scrubbing) return;
    const id = window.setInterval(() => {
      const pending = pendingTimeRef.current;
      if (pending === null) return;
      if (Date.now() - lastSeekAtRef.current < SCRUB_SEEK_THROTTLE_MS) return;
      lastSeekAtRef.current = Date.now();
      pendingTimeRef.current = null;
      onSeek(pending);
    }, SCRUB_SEEK_THROTTLE_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [scrubbing, onSeek]);

  const endScrub = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      if (trackRef.current?.hasPointerCapture(event.pointerId)) {
        trackRef.current.releasePointerCapture(event.pointerId);
      }
      if (!scrubbingRef.current) return;

      const time =
        pendingTimeRef.current ?? timeFromClientX(event.clientX) ?? displayTime;
      pendingTimeRef.current = null;
      commitSeek(time, true);
      setScrubbing(false);
      onScrubbingChange(false);
      // Keep scrubTime until the parent clears it after seek settles — avoids
      // the progress bar snapping back to the pre-seek currentTime.
      onScrubTimeChange(time);
    },
    [
      timeFromClientX,
      displayTime,
      commitSeek,
      onScrubbingChange,
      onScrubTimeChange,
    ],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      event.preventDefault();
      const time = timeFromClientX(event.clientX);
      if (time === null) return;
      trackRef.current?.setPointerCapture(event.pointerId);
      setScrubbing(true);
      onScrubbingChange(true);
      // Display intent only — the element seek happens on release (endScrub).
      // Seeking here too made every click TWO overlapping element seeks
      // (down + up), which is exactly the rapid-seek pattern that trips
      // Chrome's decoder on unbuffered ranges (PIPELINE_ERROR_DECODE).
      onScrubTimeChange(time);
    },
    [timeFromClientX, onScrubbingChange, onScrubTimeChange],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      if (!scrubbingRef.current) return;
      const time = timeFromClientX(event.clientX);
      if (time === null) return;
      onScrubTimeChange(time);
      commitSeek(time, false);
    },
    [timeFromClientX, onScrubTimeChange, commitSeek],
  );

  return (
    <div
      ref={trackRef}
      className="group/track relative flex h-4 cursor-pointer items-center"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endScrub}
      onPointerCancel={endScrub}
      role="slider"
      aria-label={t("wiedergabeposition")}
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(displayTime)}
      tabIndex={-1}
    >
      <div className="relative h-1 w-full overflow-hidden rounded-full bg-white/25 transition-[height] duration-150 group-hover/track:h-1.5">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-white/35"
          style={{ width: `${bufferedRatio * 100}%` }}
        />
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-action-500"
          style={{ width: `${progressRatio * 100}%` }}
        />
      </div>
      <span
        className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 shadow transition-opacity group-hover/track:opacity-100"
        style={{ left: `${progressRatio * 100}%` }}
      />
    </div>
  );
}
